import { useCallback, useEffect, useRef, useState } from "react";
import { streamNBAs, updateNbaStatus } from "../../../chat/services/api";
import { logConnectorFailure, logConnectorSuccess } from "../../../telemetry";
import type { RefreshTokenFn } from "../../../shared/api/fetchWithRefresh";
import type { NBAItem } from "../types";

export interface UseNbaActionsReturn {
  nbaItems: NBAItem[];
  nbaLoading: boolean;
  nbaError: string | null;
  nbaEmailsAnalysed: number;
  toggleNbaStatus: (nbaId: string | null) => void;
}

export function useNbaActions(
  apiToken: string | null,
  refreshTokens?: RefreshTokenFn,
): UseNbaActionsReturn {
  const [nbaItems, setNbaItems] = useState<NBAItem[]>([]);
  const [nbaLoading, setNbaLoading] = useState(false);
  const [nbaError, setNbaError] = useState<string | null>(null);
  const [nbaEmailsAnalysed, setNbaEmailsAnalysed] = useState(0);
  const nbaFetchedRef = useRef(false);

  const handleNbaEvent = useCallback(
    (event: { event: string; data: Record<string, unknown> }) => {
      switch (event.event) {
        case "nba_start":
          setNbaEmailsAnalysed((event.data.emails_analysed as number) ?? 0);
          break;
        case "nba_item":
          setNbaItems((prev) => [...prev, event.data as unknown as NBAItem]);
          break;
        case "nba_error":
          setNbaError((event.data.message as string) ?? "Unknown error");
          break;
        case "nba_done":
          // A scan that finds nothing is an empty result, not a failure: the
          // Actions tab merges these rows with approvals and shows its own
          // empty state only when the whole list is empty.
          break;
      }
    },
    [],
  );

  const handleFetchNBAs = useCallback(async () => {
    if (!apiToken || nbaLoading) return;
    setNbaLoading(true);
    setNbaError(null);
    setNbaItems([]);
    setNbaEmailsAnalysed(0);
    try {
      await streamNBAs(apiToken, undefined, handleNbaEvent, refreshTokens);
      logConnectorSuccess("engage", "nba-load");
    } catch (e) {
      setNbaError("Failed to load recommendations. Please try again.");
      logConnectorFailure("engage", e, "nba-load");
    } finally {
      setNbaLoading(false);
    }
  }, [apiToken, nbaLoading, handleNbaEvent, refreshTokens]);

  // Auto-trigger NBA fetch once on load when signed in
  useEffect(() => {
    if (apiToken && !nbaFetchedRef.current) {
      nbaFetchedRef.current = true;
      handleFetchNBAs();
    }
  }, [apiToken]);

  const applyNbaStatus = useCallback((nbaId: string | null, status: boolean) => {
    setNbaItems((prev) =>
      prev.map((i) => (i.nba_id === nbaId ? { ...i, is_completed: status } : i)),
    );
  }, []);

  const toggleNbaStatus = useCallback(
    async (nbaId: string | null) => {
      const item = nbaItems.find((i) => i.nba_id === nbaId);
      if (!item) return;

      const oldStatus = item.is_completed;
      const newStatus = !oldStatus;

      applyNbaStatus(nbaId, newStatus);

      if (nbaId && apiToken) {
        try {
          await updateNbaStatus(nbaId, newStatus, apiToken, refreshTokens);
          logConnectorSuccess("engage", "nba-status-update");
        } catch (e) {
          // Optimistic toggle failed — roll back the local state and log it, so
          // the silent revert (which just looks like the toggle "didn't stick")
          // is diagnosable.
          applyNbaStatus(nbaId, oldStatus);
          logConnectorFailure("engage", e, "nba-status-update");
        }
      }
    },
    [nbaItems, apiToken, applyNbaStatus, refreshTokens],
  );

  return { nbaItems, nbaLoading, nbaError, nbaEmailsAnalysed, toggleNbaStatus };
}
